import "./NewTaskModal.css";

import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { ActionButtons, Column, KanaTooltip, Modal, RadioButtonGroup, Row } from "../../../../widget";
import { useNewTaskModal } from "./useNewTaskModal";

interface NewTaskModalProps {
  show: boolean;
  // Gated separately: the two task types are created through different API paths, each with its own
  // permission, so only the types the user may actually create are offered.
  canCreateWorkflowTask: boolean;
  canCreateGeneralTask: boolean;
  /*
    Set while the caller is still resolving which types are available — the Tasks dashboard learns that
    from an org-wide query rather than from its route. Confirm waits, so the user can't submit against
    a gate that hasn't settled.
  */
  isLoading?: boolean;
  // Line under the title. Defaults to the project wording; a host whose tasks hang off something else
  // (a deal) names that instead.
  description?: string;
  onCancel: () => void;
  onConfirm: (isGeneralTask: boolean) => void;
}

export const NewTaskModal = ({
  show,
  canCreateWorkflowTask,
  canCreateGeneralTask,
  isLoading = false,
  description = "Create a new task for a project",
  onCancel,
  onConfirm,
}: NewTaskModalProps): JSX.Element => {
  const { taskType, setTaskType, taskTypeOptions, handleSubmit } = useNewTaskModal({
    show,
    canCreateWorkflowTask,
    canCreateGeneralTask,
    onConfirm,
  });

  return (
    <Modal className="NewTaskModal" show={show} title="New task" onClose={onCancel}>
      <form onSubmit={handleSubmit}>
        <Row spacingV="l">
          <Column span={12} justify="center">
            <p className="body2">{description}</p>
          </Column>
        </Row>
        {/* Fields and buttons share a fixed-width centred column (320px in the design). */}
        <div className="NewTaskModalFields">
          <RadioButtonGroup
            label="Workflow or general task"
            direction="vertical"
            data={taskTypeOptions}
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
            tooltip={<KanaTooltip tooltipHeader="" tooltipText={TaskConstants.TASK_TYPE_TOOLTIP_TEXT} />}
          />
          <ActionButtons
            fullWidth
            onCancel={onCancel}
            submitButtonLabel="Confirm"
            isLoading={isLoading}
            disabled={isLoading || taskTypeOptions.length === 0}
          />
        </div>
      </form>
    </Modal>
  );
};
